/**
 * WHAT: Hooks for client-side pagination of arrays (usePagination and
 *       usePaginationWithReset).
 * WHY:  DAMA tables routinely display hundreds or thousands of user-access
 *       records, applications, or campaign entries. These hooks manage page
 *       state, slice the data array, and expose navigation helpers so every
 *       table gets consistent pagination without duplicating index math.
 */

import { useState, useMemo, useCallback } from 'react';

/**
 * WHAT: Manages current page, items-per-page, and derived values (paginated
 *       slice, total pages, has-next/has-prev) for a given data array.
 * WHY:  Centralizes page-index arithmetic and boundary clamping so table
 *       components only need to call goToPage/nextPage/prevPage and render
 *       the returned paginatedItems.
 *
 * usePagination - A hook for managing table/list pagination state.
 * Handles page navigation, items per page, and computes paginated data.
 *
 * @param {Array} items - The full array of items to paginate
 * @param {Object} options - Configuration options
 * @param {number} options.initialPage - Starting page (default: 1)
 * @param {number} options.initialItemsPerPage - Items per page (default: 25)
 * @param {number[]} options.itemsPerPageOptions - Available page size options
 *
 * @returns {Object} Pagination state and controls
 *
 * @example
 * const {
 *   paginatedItems,
 *   currentPage,
 *   totalPages,
 *   itemsPerPage,
 *   setItemsPerPage,
 *   goToPage,
 *   nextPage,
 *   prevPage,
 *   hasNext,
 *   hasPrev,
 *   startIndex,
 *   endIndex,
 *   totalItems
 * } = usePagination(users, { initialItemsPerPage: 50 });
 *
 * // In JSX:
 * {paginatedItems.map(item => <Row key={item.id} {...item} />)}
 *
 * <button onClick={prevPage} disabled={!hasPrev}>Previous</button>
 * <span>Page {currentPage} of {totalPages}</span>
 * <button onClick={nextPage} disabled={!hasNext}>Next</button>
 */
export function usePagination(items = [], options = {}) {
  const {
    initialPage = 1,
    initialItemsPerPage = 25,
    itemsPerPageOptions = [10, 25, 50, 100]
  } = options;

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPageState] = useState(initialItemsPerPage);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(items.length / itemsPerPage));
  }, [items.length, itemsPerPage]);

  // Ensure current page is valid when items or itemsPerPage change
  const validatedCurrentPage = useMemo(() => {
    return Math.min(currentPage, totalPages);
  }, [currentPage, totalPages]);

  // Calculate start and end indices
  const startIndex = useMemo(() => {
    return (validatedCurrentPage - 1) * itemsPerPage;
  }, [validatedCurrentPage, itemsPerPage]);

  const endIndex = useMemo(() => {
    return Math.min(startIndex + itemsPerPage, items.length);
  }, [startIndex, itemsPerPage, items.length]);

  // Get paginated items
  const paginatedItems = useMemo(() => {
    return items.slice(startIndex, endIndex);
  }, [items, startIndex, endIndex]);

  // Navigation functions
  const goToPage = useCallback((page) => {
    const pageNumber = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(pageNumber);
  }, [totalPages]);

  const nextPage = useCallback(() => {
    goToPage(validatedCurrentPage + 1);
  }, [goToPage, validatedCurrentPage]);

  const prevPage = useCallback(() => {
    goToPage(validatedCurrentPage - 1);
  }, [goToPage, validatedCurrentPage]);

  const firstPage = useCallback(() => {
    goToPage(1);
  }, [goToPage]);

  const lastPage = useCallback(() => {
    goToPage(totalPages);
  }, [goToPage, totalPages]);

  // Set items per page and reset to first page
  const setItemsPerPage = useCallback((newItemsPerPage) => {
    setItemsPerPageState(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  // Check if navigation is possible
  const hasNext = validatedCurrentPage < totalPages;
  const hasPrev = validatedCurrentPage > 1;

  // Reset to first page (useful when filters change)
  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    // Data
    paginatedItems,
    totalItems: items.length,

    // Current state
    currentPage: validatedCurrentPage,
    totalPages,
    itemsPerPage,
    startIndex,
    endIndex,

    // Navigation
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    hasNext,
    hasPrev,

    // Configuration
    setItemsPerPage,
    itemsPerPageOptions,
    resetPage
  };
}

/**
 * WHAT: Wrapper around usePagination that auto-resets to page 1 when the
 *       items array reference changes.
 * WHY:  When filters change and the data set shrinks, the user may be on a
 *       page that no longer exists. This variant handles that automatically
 *       so filtered tables always start from page 1.
 *
 * usePaginationWithReset - Same as usePagination but automatically resets
 * to page 1 when items array changes (useful when filtering).
 *
 * @param {Array} items - The full array of items to paginate
 * @param {Object} options - Same options as usePagination
 * @returns {Object} Same return value as usePagination
 */
export function usePaginationWithReset(items = [], options = {}) {
  const pagination = usePagination(items, options);

  // This is handled internally by validatedCurrentPage now,
  // but we expose resetPage for manual control
  return pagination;
}

export default usePagination;
